import SparkMD5 from "spark-md5";

import { Addon } from "../components/pricing/types";

const ROOT_DOMAIN = "alliai.com";

export const EMAIL_VALIDATION_REG_EX =
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
export const PASSWORD_VALIDATION_REG_EX = /^.{6,}$/;
export const URL_VALIDATION_REG_EX =
  /^(https?:\/\/)?(www\.)?([a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)(\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\/?$/;
export const clearRound = (value: number) => {
  const cents = value * 100;

  // Sub-cent precision (usage-billed unit prices like $0.015) must survive
  // the rounding; only quantize to whole cents when nothing meaningful
  // exists below a cent (the epsilon absorbs float noise).
  if (Math.abs(cents - Math.round(cents)) > 1e-6) {
    return Math.round(value * 1e6) / 1e6;
  }

  return Math.round(cents) / 100;
};

const RTL_LANG_CODES: string[] = [
  "ar", // Arabic
  "he", // Hebrew
  "fa", // Persian (Farsi)
  "ur", // Urdu
  "ps", // Pashto
  "sd", // Sindhi
  "ks", // Kashmiri (арабский вариант)
  "ug", // Uyghur
  "ckb", // Central Kurdish (Sorani)
  "yi", // Yiddish
  "syr", // Syriac
  "nqo", // N’Ko
  "dv", // Divehi (Thaana)
];

const TITLE_PATH = "head > title";
const DESCRIPTION_PATH = "head > meta[name='description']";
const SCHEMA_PATH = "head > script[type='application/ld+json']";
const AI_PATH = "head > link[rel='alternate'][type='text/markdown']";

export function formatPrice(
  value: number | string | null | undefined,
  {
    currency = "USD",
    locale = "en-US",
    input = undefined,
    min = 2,
    max = 3,
  }: {
    currency?: string;
    locale?: string;
    input?: "cents" | undefined;
    min?: number;
    max?: number;
  } = {},
): string {
  if (value === null || value === undefined || value === "") return "";
  const result = Number(value);
  if (!Number.isFinite(result)) return "";

  const amount = input === "cents" ? result / 100 : result;

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(amount);
}

// TODO: find out if getPrice and getTotalPrice could be replaced by formatPrice
// At least two decimals, extended to the last non-zero digit so sub-cent
// prices ($0.015) don't round away to $0.02.
export const getPrice = (price: number, currency?: string) => {
  if (price === 0) {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
      style: "currency",
      currency: currency || "USD",
    }).format(clearRound(price));
  }
  if (!price) {
    return "";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
    style: "currency",
    currency: currency || "USD",
  }).format(clearRound(price / 100));
};

export const getTotalPrice = (price: number, currency?: string) => {
  if (price === 0) {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
      style: "currency",
      currency: currency || "USD",
    }).format(clearRound(price));
  }
  if (!price) {
    return "";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
    style: "currency",
    currency: currency || "USD",
  }).format(clearRound(price));
};

export const getPriceWithoutCurrency = (price: number): number => {
  return clearRound(Math.abs(price) / 100);
};

export const DATE_FORMAT = "MMMM dd',' yyyy";

// TODO: handle zero as plural (e.g. "0 pages") and support irregular forms/localization
export const pluralize = (input: string, count: number) => {
  if (!count) return input;

  if (count !== 1) {
    return `${input}s`;
  }

  return input;
};

export const sortByPrice = (sortedArray: Addon[]) =>
  sortedArray.sort((a, b) => (a.price > b.price ? 1 : -1));

export const sortByDivideBy = (sortedArray: Addon[]) =>
  sortedArray.sort((a, b) => (a.divide_by > b.divide_by ? 1 : -1));

export const withId = (v: unknown) =>
  typeof v === "number" && Number.isFinite(v) && v > 0 ? { id: v } : {};

export function calculateChecksum(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const chunkSize = 2097152; // Read in chunks of 2MB
    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();
    let cursor = 0;

    fileReader.onerror = () => {
      reject("MD5 calculation failed");
    };

    function processChunk(chunkStart: number): void {
      const chunkEnd = Math.min(chunkStart + chunkSize, file.size);
      fileReader.readAsArrayBuffer(file.slice(chunkStart, chunkEnd));
    }

    fileReader.onload = (e) => {
      if (
        e.target &&
        e.target.result &&
        e.target.result instanceof ArrayBuffer
      ) {
        spark.append(e.target.result); // Append array buffer
        cursor += chunkSize; // Move cursor

        if (cursor < file.size) {
          processChunk(cursor); // Process next chunk
        } else {
          const checksum = btoa(spark.end(true)); // Base64 encode the raw MD5 string
          resolve(checksum);
        }
      } else {
        reject("MD5 calculation failed");
      }
    };

    processChunk(0);
  });
}
export function shortenUrl(url: string): string {
  return url.replace(/(\?.*?)(?:&.*)(.{50})$/, "$1[...]$2");
}

export const formatTableDate = (date: string) => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };

  return new Date(date)
    .toLocaleDateString("en-GB", options)
    .replace(/\//g, ".");
};

export function formatNumberWithSpaces(number: number) {
  if (typeof number !== "number") return number;
  const parts = number.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  return parts.join(".");
}

export function formatNumber(
  number: number | string | null | undefined,
): string {
  if (number === null || number === undefined) return "N/A";
  const num = typeof number === "string" ? parseFloat(number) : number;
  if (isNaN(num)) return "N/A";

  return new Intl.NumberFormat("en-US").format(num);
}

export function formatNumberSmart(number: number) {
  if (number < 10000) {
    return formatNumberWithSpaces(number);
  }
  if (number < 1_000_000) {
    return (number / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  }

  return (number / 1_000_000).toFixed(1).replace(/\.0$/, "") + "m";
}
export function getLastPathSegment(pathname: string) {
  const normalizedPath = pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
  const match = normalizedPath.match(/\/([^/]+)$/);

  return match ? match[1] : "";
}

/**
 * Gets the current text color from CSS variables for use in charts
 * This ensures charts adapt to light/dark theme changes
 */
export const getChartTextColor = (): string => {
  if (typeof window === "undefined") {
    return "#0c2144"; // Default fallback for SSR
  }

  const computedStyle = getComputedStyle(document.documentElement);
  const textColor = computedStyle.getPropertyValue("--text-black").trim();

  // Fallback to default if CSS variable is not available
  return textColor || "#0c2144";
};

export function isRtl(lang?: string | null): boolean {
  if (lang) {
    return RTL_LANG_CODES.includes(lang);
  } else {
    return false;
  }
}

export function isTitleOrDescriptionCustomization({
  path,
}: {
  path?: string;
}): boolean {
  if (!path) return false;

  return [TITLE_PATH, DESCRIPTION_PATH].includes(path);
}

export function isSchemaCustomization({ path }: { path?: string }): boolean {
  return path == SCHEMA_PATH;
}

export function isAiInstructionsCustomization({
  path,
}: {
  path?: string;
}): boolean {
  return path == AI_PATH;
}

export const formatTime = (lastUpdate: string) => {
  const now = new Date();
  const lastUpdateDate = new Date(lastUpdate);
  const diffInMs = now.getTime() - lastUpdateDate.getTime();

  if (diffInMs < 60000) {
    return "just now";
  }

  const diffInMinutes = Math.ceil(diffInMs / (1000 * 60));
  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${pluralize("minute", diffInMinutes)} ago`;
  }

  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInHours = Math.floor(
    (diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );

  const days = pluralize("day", diffInDays);
  const hours = pluralize("hour", diffInHours);

  if (diffInDays > 0) {
    return `${diffInDays} ${days} and ${diffInHours} ${hours} ago`;
  } else {
    return `${diffInHours} ${hours} ago`;
  }
};

export const toUnixSeconds = (date: string | Date): number => {
  const d = date instanceof Date ? date : new Date(date);

  return Math.floor(d.getTime() / 1000);
};

export const capitalize = (str: string | null | undefined): string => {
  if (!str) return "";

  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const roundUpToMultiple = (value: number, multiple: number) => {
  if (!multiple || multiple <= 0) return value;
  if (value <= 0) return 0;

  return Math.ceil(value / multiple) * multiple;
};

export const clamp = (v: number, min: number, max: number) =>
  Math.min(Math.max(v, min), max);

export const isAlliAIDomain = (): boolean => {
  if (typeof window === "undefined") return false;

  return window.location.hostname.endsWith(ROOT_DOMAIN);
};

export const extractHostname = (rawUrl: string): string => {
  try {
    const url = rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;

    return new URL(url).hostname;
  } catch {
    return "";
  }
};
